import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { ExperienceRating } from "model";
import { useLocation } from "react-router-dom";

type FeedbackModalProviderProps = { children: ReactNode };

type FeedbackModalDisplayContextState = {
  id: string;
  isDisplayed: boolean;
  rating?: ExperienceRating;
};

type FeedbackModalDisplayContextActions = {
  resetModal: () => void;
};

type FeedbackModalDisplayContextValue = FeedbackModalDisplayContextState &
  FeedbackModalDisplayContextActions;

const URL_QUERY_PARAM_MODAL_IDENTIFIER = "modal";
const URL_QUERY_PARAM_MODAL_VALUE = "feedback";
const URL_QUERY_PARAM_RATING_IDENTIFIER = "rating";
const URL_QUERY_PARAM_RATING_VALUE_POSITIVE = "yes";
const URL_QUERY_PARAM_RATING_VALUE_NEUTRAL = "yes-with-training";
const URL_QUERY_PARAM_RATING_VALUE_NEGATIVE = "no";

const initialState = { id: "", isDisplayed: false };

const FeedbackModalDisplayContext = createContext<FeedbackModalDisplayContextValue | null>(null);

export function FeedbackModalDisplayProvider(props: FeedbackModalProviderProps) {
  // throw if attempting to use more than 1
  const feedbackModalDisplayContext = useContext(FeedbackModalDisplayContext);
  if (feedbackModalDisplayContext) throw new Error("FeedbackModalProvider must only be used once!");

  const { children } = props;

  // internal state
  const [state, setState] = useState<FeedbackModalDisplayContextState>(initialState);

  // determine if URL has related query params
  const locationQuery = useLocationQuery();
  // Determine whether the feedback modal should be displayed
  // and what rating it should start with
  const queryParamModal = locationQuery.search.get(URL_QUERY_PARAM_MODAL_IDENTIFIER);
  const queryParamRating = locationQuery.search.get(URL_QUERY_PARAM_RATING_IDENTIFIER);

  // reset state to defaults
  const resetModal = () => setState(initialState);

  // handle state updates as a side effect to control re-rendering
  useEffect(() => {
    // give feedback modal is used in multiple places so scoping to past request details
    // page using a regex: any chars after "/past-requests/" so /past-requests/abc123
    // and also providing the requestId as a modal identifier to match trigger implementation
    const match = locationQuery.pathName.match(/\/past-requests\/(.+)/);
    const requestId = match?.[1] || "";
    if (!!match && queryParamModal === URL_QUERY_PARAM_MODAL_VALUE) {
      if (queryParamRating) {
        // Note: expect query param value to be one of the following
        if (
          queryParamRating === URL_QUERY_PARAM_RATING_VALUE_POSITIVE ||
          queryParamRating === URL_QUERY_PARAM_RATING_VALUE_NEUTRAL ||
          queryParamRating === URL_QUERY_PARAM_RATING_VALUE_NEGATIVE
        ) {
          // transform string to value expected by feedback form
          const transformedRating: ExperienceRating =
            queryParamRating === URL_QUERY_PARAM_RATING_VALUE_POSITIVE
              ? "RATING_POSITIVE"
              : queryParamRating === URL_QUERY_PARAM_RATING_VALUE_NEUTRAL
                ? "RATING_NEUTRAL"
                : "RATING_NEGATIVE";
          // return value with rating
          return setState((prevState) => ({
            ...prevState,
            id: requestId,
            isDisplayed: true,
            rating: transformedRating,
          }));
        }
        // do not include rating when provided value is not recognized (does not match)
        return setState((prevState) => ({ ...prevState, id: requestId, isDisplayed: true }));
      }
      return setState((prevState) => ({ ...prevState, id: requestId, isDisplayed: true }));
    }
    return resetModal();
  }, [locationQuery.pathName, queryParamModal, queryParamRating]);

  const ContextProvider = useMemo(
    () => (
      <FeedbackModalDisplayContext.Provider value={{ ...state, resetModal }}>
        {children}
      </FeedbackModalDisplayContext.Provider>
    ),
    [children, state],
  );

  return ContextProvider;
}

// hook that can be used inside of consuming components
export function useFeedbackModalDisplay() {
  const feedbackModalDisplayContext = useContext(FeedbackModalDisplayContext);
  if (feedbackModalDisplayContext === null) {
    throw new Error("useFeedbackModalDisplay must be used within an FeedbackModalDisplayProvider!");
  }
  return feedbackModalDisplayContext;
}

// helper hook to access URL query params and path
export function useLocationQuery() {
  const { search, pathname } = useLocation();
  return useMemo(
    () => ({
      search: new URLSearchParams(search),
      pathName: pathname,
    }),
    [pathname, search],
  );
}
